import "./TwoStageSpendAndEffectContainer.scss";
import React, { useMemo, useCallback, useState } from "react";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  DownloadDropdown,
  Dropdown,
  DropdownToggleType,
  InfoTooltip,
} from "../../Components";
import { SpendAndEffectModal } from "../../Components/SpendAndEffectModal";
import { SpendAndEffectBarChart } from "../../MMM/ModelResults/SpendAndEffectBarChart";
import { exportToExcel } from "../../utils/download-utils";
import { colorMap as colors } from "../../utils/colors";
import ChartContainer from "../../Components/ChartContainer";
import { ShadedPattern } from "../FillPatterns/ShadedPattern";
import { MdZoomOutMap } from "react-icons/md";

interface TwoStageContainerProps {
  colorOverrides?: { [key: string]: string };
  dropdownOptions: readonly any[]; // DropdownOption
  dropdownValue: string;
  setDropdownValue: (value: string) => void;
  title: string;
  valueFormatter: (value: number) => string;
  twoStageData: any[];
  exportFormattedData?: any[];
  kpiType: string;
  groupBy: string;
  setGroupBy: (groupBy: string) => void;
  groupByOptions: { label: string; value: string }[];
  sortDropdownValue: string;
  setSortDropdownValue: (value: string) => void;
  modelDate: string;
}

export const TwoStageSpendandEffectContainer: React.FC<TwoStageContainerProps> = ({
  colorOverrides,
  dropdownOptions,
  dropdownValue,
  setDropdownValue,
  title,
  valueFormatter,
  twoStageData,
  exportFormattedData,
  kpiType,
  groupBy,
  setGroupBy,
  groupByOptions,
  sortDropdownValue,
  setSortDropdownValue,
  modelDate,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const excelDownload = useCallback(() => {
    const dataToExport = exportFormattedData ? exportFormattedData : twoStageData;
    exportToExcel(dataToExport, `${title.replace(/ /g, "_")}_actualized`);
  }, [title, twoStageData, exportFormattedData]);

  const twoStageColorMap = useMemo(() => {
    if (twoStageData) {
      const colorsOriginal = colors(twoStageData);

      return { ...colorsOriginal, ...colorOverrides };
    }
  }, [colorOverrides, twoStageData]);

  return (
    <>
      <div className="snapshotChartContainer twoStageSpendAndEffectContainer">
        <ChartContainer
          enableHoverDesign
          title={title}
          viewRightActions
          rightActions={
            <Button
              type={ButtonType.FILLED}
              icon={<MdZoomOutMap />}
              size="sm"
              variant={ButtonFrameworkVariant.LEADING_ICON}
              onClick={() => setShowModal(true)}
            >
              Dynamics
            </Button>
          }
          subHeader={
            <>
              <div className="subheaderText">
                <span className="label">Actualized</span>
                <InfoTooltip>
                  The spend/effect for a given tactic after making necessary corrections for demand
                  gen {"<>"} demand capture dynamics. This is the true, causal form of each figure.
                </InfoTooltip>
              </div>
              <div className="subheaderActions">
                {dropdownOptions && dropdownValue && setDropdownValue && (
                  <Dropdown
                    type={DropdownToggleType.OUTLINED}
                    design="secondary"
                    size="sm"
                    value={dropdownValue}
                    options={dropdownOptions}
                    onChange={setDropdownValue}
                  />
                )}
                <DownloadDropdown
                  size="sm"
                  onClickOptions={[excelDownload]}
                  menuOptions={["XLSX"]}
                />
              </div>
            </>
          }
        >
          <div id="snapshotGraphContents" className="snapshotGraphContents">
            <SpendAndEffectBarChart
              expandedModal={false}
              activeChannel={"All Channels"}
              barData={twoStageData}
              data={twoStageData}
              displayActualValues={false}
              valueFormatter={valueFormatter}
              colorMap={twoStageColorMap}
              barSize={20}
              groupBy={groupBy}
            />
            <div className="patternDefinitions">
              <ShadedPattern color={"grey"} visibleTag={false} />
              <ShadedPattern
                color="white"
                backgroundColor={"grey"}
                id={"Neggrey"}
                visibleTag={false}
              />
              {twoStageData.map(item => (
                <div key={`patternDefContainer${item.name}`}>
                  <ShadedPattern color={twoStageColorMap[item.name]} visibleTag={false} />
                  <ShadedPattern
                    color="white"
                    backgroundColor={twoStageColorMap[item.name]}
                    id={`Neg${twoStageColorMap[item.name]}`}
                    visibleTag={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </ChartContainer>
      </div>
      {showModal && twoStageData && dropdownOptions && (
        <SpendAndEffectModal
          valueFormatter={valueFormatter}
          data={[...twoStageData]}
          onClose={() => setShowModal(false)}
          sortOptions={[...dropdownOptions]}
          kpiType={kpiType}
          excelDownload={excelDownload}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          groupByOptions={groupByOptions}
          sortDropdownValue={sortDropdownValue}
          setSortDropdownValue={setSortDropdownValue}
          modelDate={modelDate}
        />
      )}
    </>
  );
};
